'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { PasswordField } from '@sola/ark-core/molecules/password-field/password-field.tsx';
import { PasswordRequirements } from '@sola/ark-core/molecules/password-requirements/password-requirements.tsx';
import { passwordValidator } from '@sola/ark-core/utils/helpers.ts';
import { usePasswordUpdate } from '@sola/my-sdk/settings/hooks.ts';

import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import {
  createPasswordUpdateErrorAlert,
  createPasswordUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

const PasswordModalForm = ({
  currentPassword,
  setCurrentPassword,
  onSuccess,
}: {
  currentPassword: string;
  setCurrentPassword: (value: string) => void;
  onSuccess: () => void;
}) => {
  // Resolves to the inner (modal) provider so error alerts render in the modal.
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { mutateAsync: updatePassword } = usePasswordUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    clearAlerts();
    try {
      await updatePassword({ password: data.password as string });
      onSuccess();
    } catch {
      addAlerts(createPasswordUpdateErrorAlert());
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      options={{
        defaultValues: { password: '' },
        mode: 'onChange',
      }}
    >
      <PasswordField
        name="password"
        label={t('my-settings:form.password.passwordLabel')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setCurrentPassword(e.target.value)
        }
        required
        autoComplete="new-password"
        hint={t('my-settings:form.password.caseSensitive')}
      />
      <PasswordRequirements value={currentPassword} />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary
          type="submit"
          disabled={!passwordValidator.safeParse(currentPassword).success}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalPassword = ({
  openTrigger,
}: {
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  // Captured from the outer (section) provider so success alerts surface
  // on the section after the modal closes.
  const sectionAlerts = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createPasswordUpdateSuccessAlert());
    setIsOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      onClose={() => setCurrentPassword('')}
    >
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        icon="user-edit"
        title={t('my-settings:form.password.title')}
      >
        <LocalAlertsProvider>
          <LocalAlerts />
          <PasswordModalForm
            currentPassword={currentPassword}
            setCurrentPassword={setCurrentPassword}
            onSuccess={handleSuccess}
          />
        </LocalAlertsProvider>
      </DialogContent>
    </Dialog>
  );
};
